import { Suspense, useRef } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams } from 'react-router-dom';
import { lazy } from '@/lib/lazy-route';
import { RootRouteErrorFallback } from '@/router/RootRouteErrorFallback';
import { AppLayout } from '@/components/layout/AppLayout';
import { SupportChatBubble } from '@/components/support/SupportChatBubble';
import { SpaRouteAnnouncer } from '@/components/accessibility/SpaRouteAnnouncer';
import { GlobalSimplifiedText } from '@/components/accessibility/GlobalSimplifiedText';
import ReadingMask from '@/components/accessibility/ReadingMask';
import SpeakOnSelection from '@/components/accessibility/SpeakOnSelection';
import GlobalImageHoverAudio from '@/components/accessibility/GlobalImageHoverAudio';
import { CookieConsentBanner } from '@/components/consent/CookieConsentBanner';
import { InstitutionAppThemeLayout } from '@/components/layout/InstitutionAppTheme';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { InstitutionFeatureGate } from '@/components/institution/InstitutionFeatureGate';
import { INSTITUTION_MENU_FLAGS } from '@/lib/institution-menu-flags';
import { SEGMENT_FEATURES } from '@/lib/institution-segment';
import type { UserRole } from '@/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useLanguageStore, type Language } from '@/stores/language.store';

const LANG_CODES = new Set<string>(['fr', 'en', 'lb', 'it', 'es', 'uk', 'ru', 'pt', 'de', 'ar', 'tr']);

function LanguageRedirect() {
  const { lang, '*': rest } = useParams<{ lang: string; '*': string }>();
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const isValid = lang != null && LANG_CODES.has(lang);
  const applied = useRef(false);

  if (isValid && !applied.current) {
    applied.current = true;
    setLanguage(lang as Language);
  }

  const target = isValid ? (rest ? `/${rest}` : '/') : '/';
  return <Navigate to={target} replace />;
}

/** Aligné sur `V2_RH_MODULE_ROLES` (API v2) — module RH dans l’app, hors CODIR (DRH seul). */
const RH_MODULE_APP_ROLES: string[] = ['drh', 'rh', 'resp_formation', 'institution', 'institution_admin'];

/** Écrans manager (`/team`, `/reviews`, `/reports`) : managers, module RH et admin plateforme. */
const MANAGER_SPACE_ROLES = ['manager', ...RH_MODULE_APP_ROLES, 'admin'] as UserRole[];
/** Écrans collaborateur (`/training`, `/skills`, `/catalog`) : collaborateurs, managers, module RH, admin. */
const COLLABORATOR_SPACE_ROLES = ['collaborateur', 'collaborator', ...MANAGER_SPACE_ROLES] as UserRole[];

/** Anciens liens `/v2/campaigns/...` → `/rh/campaigns/...` (API REST reste `/api/v2/campaigns`). */
function LegacyV2CampaignsRedirect() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/v2\/campaigns\/?(.*)$/);
  const rest = (m?.[1] ?? '').replace(/\/$/, '');
  const to = rest ? `/rh/campaigns/${rest}` : '/rh/campaigns';
  return <Navigate to={to} replace />;
}

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const InstitutionRegisterPage = lazy(() => import('@/pages/auth/InstitutionRegisterPage'));
const HandoffPage = lazy(() => import('@/pages/auth/HandoffPage'));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const ProducerAssignmentsPage = lazy(() => import('@/pages/dashboard/ProducerAssignmentsPage'));

// Podcasts
const PodcastsPage = lazy(() => import('@/pages/podcasts/PodcastsPage'));
const EpisodesPage = lazy(() => import('@/pages/podcasts/EpisodesPage'));
const EpisodeEditorPage = lazy(() => import('@/pages/podcasts/EpisodeEditorPage'));
const PodcastDocumentImportPage = lazy(() => import('@/pages/podcasts/PodcastDocumentImportPage'));
const PodcastPublicPage = lazy(() => import('@/pages/podcasts/PodcastPublicPage'));
const PodcastLearnerPlayPage = lazy(() => import('@/pages/podcasts/PodcastLearnerPlayPage'));

const TrainerWebinarsPage = lazy(() => import('@/pages/webinars/TrainerWebinarsPage'));
const EnterpriseWebinarsPage = lazy(() => import('@/pages/webinars/EnterpriseWebinarsPage'));
const WebinarRoomPage = lazy(() => import('@/pages/webinars/WebinarRoomPage'));

// EduFlow
const EduFlowDesignerPage = lazy(() => import('@/pages/eduflow/EduFlowDesignerPage'));
const SimpleStructurePage = lazy(() => import('@/pages/eduflow/simple/SimpleStructurePage'));
const SimpleChapterBuilderPage = lazy(() => import('@/pages/eduflow/simple/SimpleChapterBuilderPage'));
const EduFlowPlayerPage = lazy(() => import('@/pages/eduflow/EduFlowPlayerPage'));
const EduFlowParticipantPage = lazy(() => import('@/pages/eduflow/EduFlowParticipantPage'));
const EduFlowHubRedirect = lazy(() => import('@/pages/eduflow/EduFlowHubRedirect'));
const EduFlowListPage = lazy(() => import('@/pages/eduflow/EduFlowListPage'));
const EduFlowPresenterPage = lazy(() => import('@/pages/eduflow/EduFlowPresenterPage'));

// Admin
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const NetworkGroupPage = lazy(() => import('@/pages/network/NetworkGroupPage'));
const AdminInstitutionDetailPage = lazy(() => import('@/pages/admin/AdminInstitutionDetailPage'));

// Institution dashboard
const InstitutionPage = lazy(() => import('@/pages/institution/InstitutionPage'));

// Marketing
const HomePage = lazy(() => import('@/pages/marketing/HomePage'));

/** Hors ligne et connecté : la page d'accueil publique n'est pas en cache, on ouvre l'espace apprenant. */
function OfflineHomeRedirect({ children }: { children: React.ReactNode }) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const hasSession = typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('auth_token'));
  if (offline && hasSession) return <Navigate to="/learner" replace />;
  return <>{children}</>;
}
const PricingPage = lazy(() => import('@/pages/marketing/PricingPageV2'));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPageV2'));
const ContactPage = lazy(() => import('@/pages/marketing/ContactPageV2'));
const AccessibilityPage = lazy(() => import('@/pages/marketing/AccessibilityPageV2'));
const BlogPage = lazy(() => import('@/pages/marketing/BlogPage'));
const UseCasesPage = lazy(() => import('@/pages/marketing/UseCasesPageV3'));
const PartnersPage = lazy(() => import('@/pages/marketing/PartnersPageV2'));
const PrivacyPage = lazy(() => import('@/pages/marketing/PrivacyPageV2'));
const TermsPage = lazy(() => import('@/pages/marketing/TermsPageV2'));
const CookiesPage = lazy(() => import('@/pages/marketing/CookiesPageV2'));
const PublicHelpPage = lazy(() => import('@/pages/marketing/PublicHelpPage'));
const LeBrefPage = lazy(() => import('@/pages/marketing/LeBrefPage'));

// V2 RH Module
const CODIRPage = lazy(() => import('@/pages/v2/CODIRPage'));
const CampaignsPage = lazy(() => import('@/pages/v2/CampaignsPage'));
const CompetenciesPage = lazy(() => import('@/pages/v2/CompetenciesPage'));
const WorkflowsPage = lazy(() => import('@/pages/v2/WorkflowsPage'));
const CatalogBrowsePage = lazy(() => import('@/pages/v2/CatalogBrowsePage'));
const CatalogLayout = lazy(() => import('@/pages/v2/CatalogLayout'));
const CatalogItemRoutePage = lazy(() => import('@/pages/v2/CatalogItemRoutePage'));
const ManagerPage = lazy(() => import('@/pages/v2/ManagerPage'));
const MyTrainingPage = lazy(() => import('@/pages/v2/MyTrainingPage'));

// Profile & Error
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));

// Learners
const LearnerDashboardPage = lazy(() => import('@/pages/learners/LearnerDashboardPage'));
const LearnersPage = lazy(() => import('@/pages/learners/LearnersPage'));

// Parcours
const ParcoursPage = lazy(() => import('@/pages/parcours/ParcoursPage'));
const ParcoursEditorPage = lazy(() => import('@/pages/parcours/ParcoursEditorPage'));

// Learner dashboard (fullscreen)
const LearnerPage = lazy(() => import('@/pages/learner/LearnerPage'));
const LearningPathPlayerPage = lazy(() => import('@/pages/learner/LearningPathPlayerPage'));
const LearningPathPublicPlayerPage = lazy(() => import('@/pages/learner/LearningPathPublicPlayerPage'));

// Analytics
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));

// Media library
const VideosPage = lazy(() => import('@/pages/media/VideosPage'));
const VideoEditPage = lazy(() => import('@/pages/media/VideoEditPage'));
const VideoStudioPage = lazy(() => import('@/pages/media/VideoStudioPage'));
const SoundsPage = lazy(() => import('@/pages/media/SoundsPage'));
const ImagesPage = lazy(() => import('@/pages/media/ImagesPage'));
const DocumentsPage = lazy(() => import('@/pages/media/DocumentsPage'));

// Help
const HelpPage = lazy(() => import('@/pages/help/HelpPage'));

// Institution
const GroupsPage = lazy(() => import('@/pages/institution/GroupsPage'));
const BrandingPage = lazy(() => import('@/pages/institution/BrandingPage'));
const LandingPageEditorPage = lazy(() => import('@/pages/institution/LandingPageEditorPage'));
const WebhooksPage = lazy(() => import('@/pages/institution/WebhooksPage'));

// RH direct sidebar routes
const CampaignsDirectPage = lazy(() => import('@/pages/rh/CampaignsDirectPage'));
const CompetenciesDirectPage = lazy(() => import('@/pages/rh/CompetenciesDirectPage'));
const ReportsPage = lazy(() => import('@/pages/rh/ReportsPage'));
const CatalogDirectPage = lazy(() => import('@/pages/rh/CatalogDirectPage'));
const IntegrationsPage = lazy(() => import('@/pages/rh/IntegrationsPage'));
const RequestsDirectPage = lazy(() => import('@/pages/rh/RequestsDirectPage'));

// Manager
const TeamPage = lazy(() => import('@/pages/manager/TeamPage'));
const ReviewsPage = lazy(() => import('@/pages/manager/ReviewsPage'));

// Collaborator / Learner
const TrainingPage = lazy(() => import('@/pages/collaborator/TrainingPage'));
const SkillsPage = lazy(() => import('@/pages/collaborator/SkillsPage'));
const CertificatesPage = lazy(() => import('@/pages/collaborator/CertificatesPage'));

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner fullScreen />}>{children}</Suspense>
);

/** Chat support global : présent sur toutes les vues sauf pages publiques / auth (logique dans le composant). */
function RootWithSupportChat() {
  return (
    <>
      <SpaRouteAnnouncer />
      <GlobalSimplifiedText />
      <ReadingMask />
      <SpeakOnSelection />
      <GlobalImageHoverAudio />
      <Outlet />
      <SupportChatBubble />
      <CookieConsentBanner />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootWithSupportChat />,
    errorElement: <RootRouteErrorFallback />,
    children: [
  // Language prefix redirect: /fr, /en/pricing, /ar/about, etc.
  {
    path: ':lang/*',
    element: <LanguageRedirect />,
  },
  {
    path: ':lang',
    element: <LanguageRedirect />,
  },

  // Public routes with header/footer
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/',
        element: <OfflineHomeRedirect><SuspenseWrapper><HomePage /></SuspenseWrapper></OfflineHomeRedirect>,
      },
      {
        path: '/pricing',
        element: <SuspenseWrapper><PricingPage /></SuspenseWrapper>,
      },
      {
        path: '/about',
        element: <SuspenseWrapper><AboutPage /></SuspenseWrapper>,
      },
      {
        path: '/contact',
        element: <SuspenseWrapper><ContactPage /></SuspenseWrapper>,
      },
      {
        path: '/aide',
        element: <SuspenseWrapper><PublicHelpPage /></SuspenseWrapper>,
      },
      {
        path: '/accessibility',
        element: <SuspenseWrapper><AccessibilityPage /></SuspenseWrapper>,
      },
      {
        path: '/le-bref',
        element: <SuspenseWrapper><LeBrefPage /></SuspenseWrapper>,
      },
      {
        path: '/blog',
        element: <SuspenseWrapper><BlogPage /></SuspenseWrapper>,
      },
      {
        path: '/use-cases',
        element: <SuspenseWrapper><UseCasesPage /></SuspenseWrapper>,
      },
      {
        path: '/partners',
        element: <SuspenseWrapper><PartnersPage /></SuspenseWrapper>,
      },
      {
        path: '/privacy',
        element: <SuspenseWrapper><PrivacyPage /></SuspenseWrapper>,
      },
      {
        path: '/terms',
        element: <SuspenseWrapper><TermsPage /></SuspenseWrapper>,
      },
      {
        path: '/cookies',
        element: <SuspenseWrapper><CookiesPage /></SuspenseWrapper>,
      },
      {
        path: '/login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
      },
      {
        path: '/register',
        element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/establishment/register',
        element: <SuspenseWrapper><InstitutionRegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/forgot-password',
        element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
      },
      {
        path: '/reset-password',
        element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
      },
      {
        /* Lien d'invitation (élève importé, apprenant invité) : choisir son premier mot de passe. */
        path: '/set-password',
        element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
      },
    ],
  },

  // Public EduFlow routes
  {
    path: '/podcasts/public/p/:podcastId',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/public',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/learner',
    element: <SuspenseWrapper><PodcastLearnerPlayPage /></SuspenseWrapper>,
  },
  {
    /* Arrivée depuis l'app mobile : code à usage unique → session web → cours au bon bloc.
       Hors `PublicLayout` : ni télémétrie de l'URL (le code y figure), ni renvoi vers `/` sur un sous-domaine d'établissement. */
    path: '/handoff',
    element: <SuspenseWrapper><HandoffPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/flows/:flowId/public',
    element: <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/participant/:globalToken',
    element: <SuspenseWrapper><EduFlowParticipantPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:code',
    element: <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>,
  },
  {
    path: '/parcours/public',
    element: (
      <SuspenseWrapper>
        <LearningPathPublicPlayerPage />
      </SuspenseWrapper>
    ),
  },

  // Protected routes (require auth)

  // EduFlow designer & player — fullscreen (no sidebar)
  {
    path: '/eduflow/edit/:flowId',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowDesignerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eduflow/simple/:flowId/structure',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><SimpleStructurePage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eduflow/simple/:flowId/chapter/:chapterId',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><SimpleChapterBuilderPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/webinars/live/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/webinars/register/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/play',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/present',
    element: (
      <ProtectedRoute requiredRoles={['teacher', 'admin', 'institution_admin', 'resp_formation']}>
        <SuspenseWrapper><EduFlowPresenterPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Podcast episode editor — fullscreen (no sidebar)
  {
    path: '/podcasts/episodes/new',
    element: (
      <ProtectedRoute>
        <Navigate to="/podcasts/episodes?new=1" replace />
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/episodes/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EpisodeEditorPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/:podcastId/import-document',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><PodcastDocumentImportPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Video studio / editor — fullscreen (no sidebar). `studio` avant `:id` pour éviter la collision de route.
  {
    path: '/videos/studio',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><VideoStudioPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/videos/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><VideoEditPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Admin panel — fullscreen (independent layout, no AppLayout sidebar)
  {
    path: '/network',
    element: (
      <ProtectedRoute requiredRoles={['network', 'admin']}>
        <SuspenseWrapper><NetworkGroupPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <SuspenseWrapper><AdminPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/institutions/:institutionId',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <SuspenseWrapper>
          <AdminInstitutionDetailPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Learner dashboard — fullscreen (independent layout)
  {
    path: '/learner',
    element: (
      <ProtectedRoute requiredRoles={['learner', 'student', 'teacher', 'resp_formation', 'collaborateur']}>
        <SuspenseWrapper><LearnerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  // Player parcours — plein écran, sans menu apprenant (souvent ouvert dans un nouvel onglet)
  {
    path: '/learner/parcours/:assignmentId',
    element: (
      <ProtectedRoute requiredRoles={['learner', 'student', 'teacher', 'resp_formation', 'collaborateur']}>
        <SuspenseWrapper>
          <LearningPathPlayerPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/parcours/player/:assignmentId',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper>
          <LearningPathPlayerPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Institution dashboard — fullscreen (independent layout)
  {
    path: '/institution',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'admin',
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/institution/learners/:participantId',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'admin',
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/institution/learners/:participantId/bilan',
    element: <Navigate to=".." relative="path" replace />,
  },

  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper>,
      },
      {
        path: 'producer-assignments',
        element: (
          <ProtectedRoute requiredRoles={['teacher', 'resp_formation', 'institution_admin']}>
            <SuspenseWrapper><ProducerAssignmentsPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper>,
      },

      // Liste des formations (formateur / rôles création)
      {
        path: 'eduflow/list',
        element: <SuspenseWrapper><EduFlowListPage /></SuspenseWrapper>,
      },

      // Ancienne URL /eduflow → redirection (établissement → onglet flows, sinon liste formations)
      {
        path: 'eduflow',
        element: <SuspenseWrapper><EduFlowHubRedirect /></SuspenseWrapper>,
      },

      // Podcasts (list pages only — editor is fullscreen outside AppLayout)
      {
        path: 'podcasts',
        element: <SuspenseWrapper><PodcastsPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/episodes',
        element: <SuspenseWrapper><EpisodesPage /></SuspenseWrapper>,
      },

      {
        path: 'webinars',
        element: (
          <ProtectedRoute
            requiredRoles={[
              'teacher',
              'resp_formation',
              'collaborateur',
              'collaborator',
              'manager',
              'institution_admin',
            ]}
          >
            <SuspenseWrapper><TrainerWebinarsPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'webinars/enterprise',
        element: (
          <ProtectedRoute
            requiredRoles={['institution', 'institution_admin', 'drh', 'rh', 'resp_formation']}
          >
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.webinars_enterprise}>
              <SuspenseWrapper><EnterpriseWebinarsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },

      // Learners
      {
        path: 'learners/:participantId',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <SuspenseWrapper><LearnerDashboardPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'learners',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <SuspenseWrapper><LearnersPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },

      // Parcours
      {
        path: 'parcours',
        element: <SuspenseWrapper><ParcoursPage /></SuspenseWrapper>,
      },
      {
        path: 'parcours/:id/edit',
        element: <SuspenseWrapper><ParcoursEditorPage /></SuspenseWrapper>,
      },

      // Analytics
      {
        path: 'analytics',
        element: <SuspenseWrapper><AnalyticsPage /></SuspenseWrapper>,
      },

      // Media library
      {
        path: 'videos',
        element: <SuspenseWrapper><VideosPage /></SuspenseWrapper>,
      },
      {
        path: 'sounds',
        element: <SuspenseWrapper><SoundsPage /></SuspenseWrapper>,
      },
      {
        path: 'images',
        element: <SuspenseWrapper><ImagesPage /></SuspenseWrapper>,
      },
      {
        path: 'documents',
        element: <SuspenseWrapper><DocumentsPage /></SuspenseWrapper>,
      },

      // Help
      {
        path: 'help',
        element: <SuspenseWrapper><HelpPage /></SuspenseWrapper>,
      },

      // Institution
      {
        path: 'groups',
        element: <SuspenseWrapper><GroupsPage /></SuspenseWrapper>,
      },
      {
        path: 'branding',
        element: <SuspenseWrapper><BrandingPage /></SuspenseWrapper>,
      },
      {
        path: 'landing-page',
        element: <SuspenseWrapper><LandingPageEditorPage /></SuspenseWrapper>,
      },
      {
        path: 'webhooks',
        element: <SuspenseWrapper><WebhooksPage /></SuspenseWrapper>,
      },

      // Direct sidebar routes for DRH/RH roles
      {
        path: 'campaigns',
        element: (
          <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_campaigns}>
            <SuspenseWrapper><CampaignsDirectPage /></SuspenseWrapper>
          </InstitutionFeatureGate>
        ),
      },
      {
        path: 'competencies',
        element: (
          <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_competencies}>
            <SuspenseWrapper><CompetenciesDirectPage /></SuspenseWrapper>
          </InstitutionFeatureGate>
        ),
      },
      {
        path: 'compliance',
        element: <Navigate to="/institution" replace />,
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredRoles={MANAGER_SPACE_ROLES}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.rh_reports}>
              <SuspenseWrapper><ReportsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalog',
        element: (
          <ProtectedRoute requiredRoles={COLLABORATOR_SPACE_ROLES}>
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_catalog}>
              <SuspenseWrapper><CatalogDirectPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'integrations',
        element: (
          <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.integrations_page}>
            <SuspenseWrapper><IntegrationsPage /></SuspenseWrapper>
          </InstitutionFeatureGate>
        ),
      },
      {
        path: 'requests',
        element: (
          <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_requests}>
            <SuspenseWrapper><RequestsDirectPage /></SuspenseWrapper>
          </InstitutionFeatureGate>
        ),
      },

      // Manager direct routes
      {
        path: 'team',
        element: (
          <ProtectedRoute requiredRoles={MANAGER_SPACE_ROLES}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.manager_space}>
              <SuspenseWrapper><TeamPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reviews',
        element: (
          <ProtectedRoute requiredRoles={MANAGER_SPACE_ROLES}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.manager_space}>
              <SuspenseWrapper><ReviewsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },

      // Collaborator / Learner direct routes
      {
        path: 'training',
        element: (
          <ProtectedRoute requiredRoles={COLLABORATOR_SPACE_ROLES}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.collaborator_space}>
              <SuspenseWrapper><TrainingPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'skills',
        element: (
          <ProtectedRoute requiredRoles={COLLABORATOR_SPACE_ROLES}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.collaborator_space}>
              <SuspenseWrapper><SkillsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'certificates',
        element: <SuspenseWrapper><CertificatesPage /></SuspenseWrapper>,
      },

      // Settings
      {
        path: 'settings',
        element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper>,
      },

      // V2 RH Module (ancien /rh/dashboard → espace établissement)
      {
        path: 'rh/dashboard',
        element: <Navigate to="/institution" replace />,
      },
      {
        path: 'rh/codir',
        element: (
          <ProtectedRoute requiredRoles={['drh']}>
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_codir}>
              <SuspenseWrapper><CODIRPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'v2/campaigns/*',
        element: (
          <ProtectedRoute requiredRoles={RH_MODULE_APP_ROLES}>
            <LegacyV2CampaignsRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/campaigns/*',
        element: (
          <ProtectedRoute requiredRoles={RH_MODULE_APP_ROLES}>
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_campaigns}>
              <SuspenseWrapper><CampaignsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/competencies/*',
        element: (
          <ProtectedRoute requiredRoles={RH_MODULE_APP_ROLES}>
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_competencies}>
              <SuspenseWrapper><CompetenciesPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      {
        path: 'rh/compliance/*',
        element: <Navigate to="/institution" replace />,
      },
      {
        path: 'rh/requests/*',
        element: (
          <ProtectedRoute requiredRoles={RH_MODULE_APP_ROLES}>
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_requests}>
              <SuspenseWrapper><WorkflowsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
      /** Ancien module budget (retiré) : liens bookmarkés → sous-module compétences institution. */
      {
        path: 'rh/budgets',
        element: <Navigate to="/institution?tab=rh-module&rh=competencies" replace />,
      },
      {
        path: 'rh/catalog',
        element: (
          <ProtectedRoute
            requiredRoles={['drh', 'rh', 'resp_formation', 'institution', 'institution_admin', 'manager', 'collaborateur']}
          >
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.rh_catalog}>
              <SuspenseWrapper><CatalogLayout /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <SuspenseWrapper><CatalogBrowsePage /></SuspenseWrapper>,
          },
          {
            path: ':itemId',
            element: <SuspenseWrapper><CatalogItemRoutePage /></SuspenseWrapper>,
          },
        ],
      },

      // Manager routes
      {
        path: 'manager/*',
        element: (
          <ProtectedRoute requiredRoles={['manager']}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.manager_space}>
              <SuspenseWrapper><ManagerPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },

      // Collaborator routes
      {
        path: 'my-training/*',
        element: (
          <ProtectedRoute requiredRoles={['collaborateur', 'manager']}>
            <InstitutionFeatureGate feature={SEGMENT_FEATURES.collaborator_space}>
              <SuspenseWrapper><MyTrainingPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },
    ],
  },

  // 404
  {
    path: '*',
    element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
  },
    ],
  },
]);

export function AppRouter() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default AppRouter;
